import { DownloadOutlined } from '@ant-design/icons';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Flex,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { LeadDaLista } from '@titularis/api-client';
import { useEstadoDeLista } from '@titularis/ui';
import { baixarBlob } from '../../arquivo';
import { api } from '../../api';
import { formatarData } from '../../dominio';
import {
  buscarListasParaCsv,
  montarCsvDasListas,
  nomeDoCsvDasListas,
  ROTULOS_DAS_LISTAS,
  rotuloDaLista,
  rotuloDaOrigem,
  type RecorteDasListas,
} from './listas';

/**
 * Listas de espera: quem pediu para ser avisado do que ainda não existe.
 *
 * **Era uma tela à parte até 25/09/2026**, com outra permissão e outra régua.
 * Ela é o degrau "Na lista" do funil visto por dentro, e morando longe dele os
 * dois números divergiam sem aviso: os cartões de lá contavam a equipe que
 * testou o formulário, e o degrau não. Aqui a régua é a do funil - as contas
 * internas ficam de fora por padrão, pelo mesmo parâmetro da aba Pessoas -, e a
 * soma dos cartões **é** o degrau, porque a API calcula os dois pela mesma
 * consulta.
 *
 * O resumo vem primeiro porque a decisão é qual módulo construir a seguir.
 * Contar só a página aberta, ou incluir quem se descadastrou, distorceria essa
 * prioridade. Os cartões seguem mostrando todas as listas quando a tabela está
 * filtrada, para compará-las.
 *
 * **A tabela é o histórico do pedido.** Quem saiu continua nela, com a data da
 * saída, e também no CSV: apagar essa distinção faria o arquivo parecer uma
 * lista de destinatários autorizados. A situação fica explícita nos dois.
 *
 * Os recortes são o tópico e as contas internas, junto da página na URL. Busca
 * por e-mail e data não ajudam a escolher o próximo módulo e acrescentariam
 * controles que este trabalho não pede.
 */
const PAGE_SIZE = 50;

export function ListasDeEspera() {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const { ler, filtrar, irParaPagina, busca: enderecoAtual } = useEstadoDeLista();
  const topico = ler.texto('topico') || undefined;
  const pagina = ler.pagina();
  /*
   * O mesmo parâmetro da aba Pessoas, e de propósito: "mostrar contas internas"
   * quer dizer a mesma coisa nas duas, e trocar de aba não deveria desfazer a
   * escolha.
   */
  const semInternas = ler.texto('internas') !== 'mostrar';

  const resumo = useQuery({
    queryKey: ['resumo-da-lista-de-espera', { semInternas }],
    queryFn: () => api.resumoDaListaDeEspera(semInternas ? { semInternas } : {}),
  });

  const inscricoes = useQuery({
    queryKey: ['lista-de-espera', { topico, semInternas, pagina }],
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.listaDeEspera({
        ...(topico ? { topic: topico } : {}),
        ...(semInternas ? { semInternas } : {}),
        page: pagina,
        pageSize: PAGE_SIZE,
      }),
  });

  /*
   * Zero também informa a prioridade. As quatro listas conhecidas ficam mesmo
   * sem interessados, e as novas entram conforme aparecem no resumo. Durante
   * a busca há esqueletos; erro não vira uma demanda de zero pessoas.
   */
  const totais = new Map<string, number>(
    [...ROTULOS_DAS_LISTAS.keys()].map((codigo) => [codigo, 0]),
  );
  for (const item of resumo.data ?? []) totais.set(item.topic, item.total);

  /*
   * O resumo só conhece quem continua na lista. A página e a URL completam o
   * seletor para um tópico com apenas descadastrados também poder ser revisto.
   * Conferir o filtro contra o mapa fixo recusaria tópicos novos do servidor.
   */
  const topicos = new Set([
    ...totais.keys(),
    ...(inscricoes.data?.itens ?? []).map((linha) => linha.topic),
    ...(topico ? [topico] : []),
  ]);

  const exportacao = useMutation({
    // O recorte fica preso ao clique, mesmo se o filtro mudar durante a busca.
    mutationFn: async (recorte: RecorteDasListas) => {
      const todas = await buscarListasParaCsv(api.listaDeEspera, recorte);
      const arquivo = new Blob([montarCsvDasListas(todas)], { type: 'text/csv;charset=utf-8;' });
      baixarBlob(arquivo, nomeDoCsvDasListas(recorte.topico));
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  /*
   * A ficha leva o endereço atual no `de`, como a lista de Pessoas faz: quem
   * volta dela cai nesta aba, no mesmo tópico e na mesma página.
   */
  const fichaDe = (id: string) => {
    const ficha = `/pessoas/${encodeURIComponent(id)}`;
    return enderecoAtual ? `${ficha}?de=${encodeURIComponent(enderecoAtual)}` : ficha;
  };

  const colunas: TableColumnsType<LeadDaLista> = [
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: 'Lista',
      dataIndex: 'topic',
      key: 'topic',
      width: 180,
      ellipsis: true,
      render: (valor: string) => rotuloDaLista(valor),
    },
    {
      title: 'Origem',
      dataIndex: 'source',
      key: 'source',
      width: 150,
      render: (valor: string) => rotuloDaOrigem(valor),
    },
    {
      title: 'Conta',
      key: 'userId',
      width: 110,
      /*
       * A ponte que a tela à parte não tinha: quem tem conta tem ficha, e a
       * ficha diz o que essa pessoa fez depois de pedir para ser avisada.
       */
      render: (_: unknown, linha: LeadDaLista) =>
        linha.userId ? (
          <Link to={fichaDe(linha.userId)}>Ver ficha</Link>
        ) : (
          <Typography.Text type="secondary">sem conta</Typography.Text>
        ),
    },
    {
      title: 'Entrou em',
      dataIndex: 'createdAt',
      key: 'createdAt',
      // 180, e não 160: "25/09/2026, 17:52" mede 132px mais 32 de respiro, e a
      // 160 a hora caía para a segunda linha e dobrava a altura de toda linha.
      width: 180,
      render: (valor: string) => formatarData(valor),
    },
    {
      title: 'Situação',
      key: 'unsubscribedAt',
      width: 220,
      render: (_: unknown, linha: LeadDaLista) =>
        linha.unsubscribedAt ? (
          <Tag color="default">saiu em {formatarData(linha.unsubscribedAt)}</Tag>
        ) : (
          <Tag color="green">na lista</Tag>
        ),
    },
  ];

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        Quem pediu para ser avisado do que ainda não existe. Os cartões contam só quem continua na
        lista, e sem as contas internas a soma deles é o degrau "Na lista" do funil. A tabela mostra
        também quem saiu.
      </Typography.Text>

      {resumo.error ? (
        <Alert type="error" showIcon title={resumo.error.message} />
      ) : (
        <Row gutter={[token.margin, token.margin]}>
          {[...totais].map(([codigo, total]) => (
            <Col key={codigo} xs={12} xl={6}>
              <Card size="small" loading={resumo.isPending}>
                <Statistic title={rotuloDaLista(codigo)} value={total} />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Os recortes à esquerda e a ação à direita, como na aba Acessos. */}
      <Flex justify="space-between" align="center" wrap gap={token.marginSM}>
        <Flex gap={token.marginSM} wrap>
          <Select
            allowClear
            aria-label="Filtrar por lista"
            placeholder="Todas as listas"
            style={{ minWidth: 240 }}
            value={topico}
            onChange={(valor: string | undefined) => filtrar({ topico: valor })}
            options={[...topicos].map((codigo) => ({
              value: codigo,
              label: rotuloDaLista(codigo),
            }))}
          />
          <Tooltip title='Inscrições com o e-mail de uma conta da equipe ou de teste. Ficam fora do degrau "Na lista", e por isso ficam fora daqui por padrão.'>
            <Checkbox
              checked={!semInternas}
              onChange={(evento) =>
                filtrar({ internas: evento.target.checked ? 'mostrar' : undefined })
              }
              style={{ alignSelf: 'center' }}
            >
              Mostrar contas internas
            </Checkbox>
          </Tooltip>
        </Flex>

        <Button
          icon={<DownloadOutlined />}
          loading={exportacao.isPending}
          onClick={() => exportacao.mutate({ topico, semInternas })}
        >
          Exportar CSV
        </Button>
      </Flex>

      {inscricoes.error ? (
        <Alert type="error" showIcon title={inscricoes.error.message} />
      ) : null}

      <Card styles={{ body: { padding: 0 } }}>
        <Table<LeadDaLista>
          rowKey="id"
          dataSource={inscricoes.data?.itens ?? []}
          columns={colunas}
          loading={inscricoes.isFetching}
          locale={{
            emptyText: (
              <Empty
                description={
                  topico
                    ? 'Ninguém pediu para ser avisado nesta lista.'
                    : 'Ninguém pediu para ser avisado ainda.'
                }
              />
            ),
          }}
          pagination={{
            current: pagina,
            pageSize: PAGE_SIZE,
            total: inscricoes.data?.total ?? 0,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (proxima: number) => irParaPagina(proxima),
          }}
          // Com x: true, a coluna flexível esticava a tabela e escondia a situação.
          scroll={{ x: 1000 }}
        />
      </Card>
    </Space>
  );
}
